import AccessAlarmsOutlinedIcon from '@mui/icons-material/AccessAlarmsOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    styled,
    Typography,
    useTheme,
} from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBetween from 'components/flexbox/FlexBetween';
import FlexBox from 'components/flexbox/FlexBox';
import useBoolean from 'hooks/useBoolean';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import FormatDate from 'utils/formatDate';
import CheckRounded from '@mui/icons-material/CheckRounded';

const StyledSmall = styled('span')(({ theme, type }) => ({
    display: 'inline-block',
    fontSize: 12,
    color: theme.palette.common.white,
    padding: '4px 10px',
    borderRadius: 8,
    fontWeight: 700,
    backgroundColor:
        type === 'success' ? theme.palette.success.main : theme.palette.primary.main,
}));

const PaymentCompleteAdditional = () => {
    const [dataInvoice, setDataInvoice] = useState();
    const navigate = useNavigate();
    const theme = useTheme();

    const openModalPayment = useBoolean();
    const openModalCancel = useBoolean();
    const isProcessing = useBoolean();

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        await axiosInstance
            .get('/api/prv/member-invoices', {
                params: {
                    packageType: 'additional',
                },
            })
            .then((response) => {
                handleResponse(response);
            })
            .catch((err) => console.log(err));
    };

    const handleResponse = (response) => {
        if (response.data.length === 0) {
            navigate('/dashboard/choose-additional-workshop');
            return;
        }

        const pendingInvoice = response.data.find(
            (item) => 'pending' === item.status.value,
        );
        
        if (pendingInvoice) {
            setDataInvoice(pendingInvoice);
            return;
        }

        navigate('/dashboard/choose-additional-workshop');
    };

    const CancelInvoice = async (id) => {
        isProcessing.onTrue();

        await axiosInstance
            .patch(`/api/prv/member-invoices/${id}`, { status: 'cancel' })
            .then(() => {
                getData();
            })
            .catch((err) => console.log(err))
            .finally(() => {
                isProcessing.onFalse();
                openModalCancel.onFalse();
            });
    };

    const downloadInvoice = async () => {
        isProcessing.onTrue();
        await downloadFile(`/api/prv/member-invoices/download/${dataInvoice?.id}`);
        isProcessing.onFalse();
    };

    const PendingAction = () => (
        <>
            <Typography variant="h6" fontWeight={700} mt={4}>
                Please complete your payment
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="body1" color="text.error">
                    Invoice Amount:
                </Typography>
                <Typography variant="body1" color="text.error" fontWeight={700}>
                    {formatCurrency(dataInvoice?.totalPrice)}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="body1" color="text.error">
                    Due Date Payment:
                </Typography>
                <Typography variant="body1" color="text.error" fontWeight={700}>
                    {FormatDate(dataInvoice?.expiryDate, 'dd MMMM yyyy HH:mm')}
                </Typography>
            </Box>

            <Stack spacing={2} mt={7}>
                <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => openModalPayment.onTrue()}
                >
                    Pay Online Now
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    color="error"
                    fullWidth
                    onClick={() => openModalCancel.onTrue()}
                >
                    Cancel Invoice
                </Button>
            </Stack>
        </>
    );

    const PaidSection = () => (
        <Stack mt={7} spacing={2} alignItems="center">
            <Box
                sx={{
                    width: 84,
                    height: 84,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                }}
            >
                <CheckRounded sx={{ fontSize: 56, color: 'common.white' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} align="center" lineHeight={1.2}>
                Thank you, we have received your payment
            </Typography>
            <Typography variant="body1" align="center">
                Your transaction was successful
            </Typography>
        </Stack>
    );

    return (
        <DashboardLayout>
            {dataInvoice && (
                <>
                    <Dialog
                        fullScreen
                        open={openModalPayment.value}
                        onClose={() => openModalPayment.setFalse()}
                        aria-labelledby="payment-dialog-title"
                        aria-describedby="payment-dialog-description"
                    >
                        <DialogTitle
                            id="payment-dialog-title"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            Online Payment
                            <Button
                                onClick={() => openModalPayment.onFalse()}
                                variant="outlined"
                                startIcon={<ArrowBackIosNewOutlinedIcon />}
                            >
                                Back
                            </Button>
                        </DialogTitle>
                        <DialogContent>
                            <iframe
                                src={dataInvoice.invoiceUrl}
                                style={{ border: 0, height: '100vh' }}
                                width="100%"
                                title="additional-invoice"
                            ></iframe>
                        </DialogContent>

                        <DialogActions></DialogActions>
                    </Dialog>

                    <AppModal
                        open={openModalCancel.value}
                        handleClose={() => openModalCancel.onFalse()}
                    >
                        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
                            <Box
                                sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.palette.error.main,
                                    backgroundColor: theme.palette.warning.light,
                                }}
                            >
                                <ErrorOutlineOutlinedIcon fontSize="large" />
                            </Box>

                            <Typography variant="h6" fontWeight={800}>
                                Cancel this invoice?
                            </Typography>
                            <Typography variant="body1" color="text.error">
                                This action cannot be undone. Invoice{' '}
                                <strong>{dataInvoice.number}</strong> will be removed and any
                                reserved workshops released.
                            </Typography>

                            <FlexBox
                                alignItems="center"
                                justifyContent="center"
                                gap={2}
                                mt={1}
                                sx={{ width: '100%' }}
                            >
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => openModalCancel.onFalse()}
                                >
                                    Keep Invoice
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    loading={isProcessing.value}
                                    onClick={() => CancelInvoice(dataInvoice.id)}
                                >
                                    {isProcessing.value ? 'Cancelling...' : 'Cancel Anyway'}
                                </LoadingButton>
                            </FlexBox>
                        </Stack>
                    </AppModal>

                    <Box pt={8} pb={4}>
                        <Card
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                border: `1px solid ${theme.palette.divider}`,
                                transition:
                                    'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                                backgroundColor: theme.palette.background.paper,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0 }}>
                                        Invoice Number
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {dataInvoice.number}
                                    </Typography>
                                </Box>
                                <Box>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        endIcon={<FileDownloadOutlinedIcon />}
                                        onClick={() => downloadInvoice()}
                                        loading={isProcessing.value}
                                    >
                                        {isProcessing.value ? 'Loading...' : 'Download Invoice'}
                                    </LoadingButton>
                                </Box>
                            </Box>
                            <Grid container spacing={3}>
                                <Grid item md={6} xs={12}>
                                    <FlexBetween my={3}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body1" color="text.primary">
                                                Bill To:
                                            </Typography>
                                            <Typography variant="h6" fontWeight={800}>
                                                {dataInvoice.fullName}
                                            </Typography>
                                            <Typography variant="body2" color="text.main">
                                                {dataInvoice.profession}
                                            </Typography>
                                        </Stack>

                                        <Box textAlign="right">
                                            <Typography variant="body2" color="text.secondary">
                                                {dataInvoice.institution}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {dataInvoice.city}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {dataInvoice.country}
                                            </Typography>
                                        </Box>
                                    </FlexBetween>

                                    <Divider />

                                    <Typography variant="h6" color="text.primary" mt={2}>
                                        Selected Workshops:
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {dataInvoice?.workshops?.map((workshop) => (
                                            <Stack spacing={0.5} key={workshop.id}>
                                                <Typography
                                                    variant="body1"
                                                    color="text.main"
                                                    fontWeight={600}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.4,
                                                    }}
                                                >
                                                    {workshop.name}
                                                    <Typography variant="body1" color="text.secondary">
                                                        ({workshop.workshopType.label} Workshop)
                                                    </Typography>
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <CalendarMonthOutlinedIcon size="small" />
                                                    {workshop.date}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <AccessAlarmsOutlinedIcon size="small" />
                                                    {workshop.time}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Grid>

                                <Grid item md={6} xs={12}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            borderRadius: 3,
                                            boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    >
                                        <Stack
                                            spacing={2}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent={'space-between'}
                                        >
                                            Status Invoice :
                                            <Chip
                                                label={dataInvoice.status.label}
                                                sx={{
                                                    backgroundColor: dataInvoice.status.bgColor,
                                                    color: dataInvoice.status.color,
                                                    fontWeight: 600,
                                                }}
                                                size="small"
                                            />
                                        </Stack>

                                        {'paid' === dataInvoice.status.value && <PaidSection />}
                                        {'pending' === dataInvoice.status.value && <PendingAction />}
                                    </Card>
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>
                </>
            )}
        </DashboardLayout>
    );
};

export default PaymentCompleteAdditional;
